import React from 'react';
import { Star } from 'lucide-react';
import { SellerRatingBreakdownDto } from '../../types';
import { useTranslation } from '../../context/TranslationContext';
import { translateWithFallback } from '../../utils/translateWithFallback';

interface ReviewSummaryCardProps {
    breakdown: SellerRatingBreakdownDto;
}

const ReviewSummaryCard: React.FC<ReviewSummaryCardProps> = ({ breakdown }) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const total = breakdown.ratingCount || 0;
    const counts = breakdown.countsByStar?.length === 5
        ? breakdown.countsByStar
        : [0, 0, 0, 0, 0];

    return (
        <div className="mb-6 flex flex-col gap-6 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] bg-[color-mix(in_srgb,var(--bg-color),white_20%)] p-5 sm:flex-row sm:items-center">
            <div className="flex flex-col items-center sm:min-w-[100px]">
                <p className="text-4xl font-bold text-[var(--text-color)]">
                    {total > 0 ? breakdown.averageRating.toFixed(1) : '-'}
                </p>
                <div className="mt-1 flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                            key={n}
                            className={`h-3.5 w-3.5 ${
                                n <= Math.round(breakdown.averageRating)
                                    ? 'fill-[var(--accent-color)] text-[var(--accent-color)]'
                                    : 'text-[color-mix(in_srgb,var(--text-color),transparent_65%)]'
                            }`}
                        />
                    ))}
                </div>
                <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                    {total} {tr('profile.stats.reviews', 'reviews')}
                </p>
            </div>

            <div className="flex-1 space-y-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                    const count = counts[star - 1] ?? 0;
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                        <div key={star} className="flex items-center gap-2 text-xs">
                            <span className="w-3 text-[var(--text-color)]">{star}</span>
                            <Star className="h-3 w-3 fill-[var(--accent-color)] text-[var(--accent-color)]" />
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)]">
                                <div
                                    className="h-full rounded-full bg-[var(--accent-color)] transition-all"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <span className="w-8 text-right text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {pct}%
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ReviewSummaryCard;
